import { StargraveCrewData, StargraveLeader, StargraveSoldier } from '../types';
import { stargraveAdvancedTechnology } from '../data/stargrave/advancedTechnology';
import { stargraveAdvancedWeapons } from '../data/stargrave/advancedWeapons';
import { getAllStargraveEquipment, getAllStargraveSoldiers } from '../stores/stargraveCustomStore';

export type StargraveUpkeepDecision = 'pay' | 'waived' | 'free' | 'unpaid';

export interface StargraveUpkeepSuit {
  /** Stable decision key: gear:<leaderId>:<gearId> | tech:<instanceId> | soldier:<soldierId>:<gearId>. */
  key: string;
  ownerId: string;
  ownerName: string;
  ownerKind: 'leader' | 'soldier';
  itemName: string;
  cost: number;
}

/** All fielded combat-armour suits that require per-game upkeep. */
export function getStargraveUpkeepSuits(crew: StargraveCrewData): StargraveUpkeepSuit[] {
  const equipmentById = new Map(getAllStargraveEquipment().map((item) => [item.id, item]));
  const profiles = getAllStargraveSoldiers();
  const leaders = [crew.captain, crew.firstMate].filter((leader): leader is StargraveLeader => Boolean(leader));
  const fieldedSoldiers = crew.soldiers.filter((soldier) => !soldier.storedInShip && !soldier.isTemporary);
  const suits: StargraveUpkeepSuit[] = [];

  for (const leader of leaders) {
    for (const gearId of leader.gearIds) {
      const item = equipmentById.get(gearId);
      if (item?.upkeepCost) {
        suits.push({ key: `gear:${leader.id}:${gearId}`, ownerId: leader.id, ownerName: leader.name, ownerKind: 'leader', itemName: item.name, cost: item.upkeepCost });
      }
    }
  }

  for (const instance of crew.advancedTechnology) {
    const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
    if (!technology?.upkeepCost || !instance.ownerId) continue;
    const leader = leaders.find((candidate) => candidate.id === instance.ownerId);
    const soldier = leader ? undefined : fieldedSoldiers.find((candidate) => candidate.id === instance.ownerId);
    const owner = leader ?? soldier;
    if (!owner) continue;
    suits.push({ key: `tech:${instance.instanceId}`, ownerId: owner.id, ownerName: owner.name, ownerKind: leader ? 'leader' : 'soldier', itemName: technology.name, cost: technology.upkeepCost });
  }

  for (const soldier of fieldedSoldiers) {
    const profile = profiles.find((candidate) => candidate.id === soldier.profileId);
    const gearId = profile?.equipmentIds.find((id) => equipmentById.get(id)?.upkeepCost);
    if (!gearId) continue;
    const item = equipmentById.get(gearId)!;
    suits.push({ key: `soldier:${soldier.id}:${gearId}`, ownerId: soldier.id, ownerName: soldier.name, ownerKind: 'soldier', itemName: item.name, cost: item.upkeepCost! });
  }

  return suits;
}

/** Armoury power waives one suit's upkeep; two with the Armament Workshop upgrade. */
export function getStargraveUpkeepWaiverLimit(crew: StargraveCrewData): number {
  return crew.ship.upgradeIds.includes('armament-workshop') ? 2 : 1;
}

/** Activation-bonus hints from owned ship upgrades, by power ID. */
export function getShipUpgradePowerHints(crew: StargraveCrewData, powerId: string): string[] {
  const owned = crew.ship.upgradeIds;
  const hints: string[] = [];
  if (powerId === 'bribe' && owned.includes('communications-array')) hints.push('+2 Activation (Communications Array)');
  if (powerId === 'mystic-trance' && owned.includes('meditation-chamber')) hints.push('+2 Activation (Meditation Chamber)');
  if (['create-robot', 'remote-guidance', 're-wire-robot'].includes(powerId) && owned.includes('robotics-workshop')) {
    hints.push('+1 to one Out of Game activation (Robotics Workshop)');
  }
  return hints;
}

export function getStargraveUpkeepCost(
  suits: StargraveUpkeepSuit[],
  decisions: Record<string, StargraveUpkeepDecision>
): number {
  return suits.reduce((total, suit) => total + ((decisions[suit.key] ?? 'pay') === 'pay' ? suit.cost : 0), 0);
}

/** Whether a crew still has an Armoury power available (captain or first mate). */
export function crewHasArmouryPower(crew: StargraveCrewData): boolean {
  return [crew.captain, crew.firstMate].some((leader) =>
    leader?.powers.some((selection) => selection.powerId === 'armoury'));
}

export type StargraveUpkeepReplacement = 'recruit' | 'runner';

export interface StargraveArmouryWeaponBoost {
  memberId: string;
  weaponId: string;
}

export interface StargraveArmourUpkeepOptions {
  decisions: Record<string, StargraveUpkeepDecision>;
  /** Temporary Recruit/Runner replacements for unpaid soldier suits, by soldier ID. */
  replacements?: Record<string, StargraveUpkeepReplacement>;
  /** Armoury +1 Damage weapon boosts; share the waiver budget. */
  weaponBoosts?: StargraveArmouryWeaponBoost[];
}

export const isFieldedStargraveSoldier = (soldier: StargraveSoldier): boolean =>
  !soldier.storedInShip && !soldier.gameState.sittingOut;

const ARMOURY_BOOSTABLE_WEAPON_IDS = ['pistol', 'carbine', 'shotgun'];

export interface StargraveArmouryBoostOption {
  memberId: string;
  memberName: string;
  weaponId: string;
  weaponName: string;
}

/** Standard pistols/carbines/shotguns eligible for the Armoury +1 Damage boost
 *  (Advanced Technology weapons and replaced base weapons are excluded). */
export function getArmouryBoostableWeapons(crew: StargraveCrewData): StargraveArmouryBoostOption[] {
  const equipmentById = new Map(getAllStargraveEquipment().map((item) => [item.id, item]));
  const profiles = getAllStargraveSoldiers();
  const leaders = [crew.captain, crew.firstMate].filter((leader): leader is StargraveLeader => Boolean(leader));
  const replacedBaseWeaponIds = (ownerId: string) => new Set(
    crew.advancedWeapons
      .filter((instance) => instance.ownerId === ownerId)
      .map((instance) => stargraveAdvancedWeapons.find((weapon) => weapon.id === instance.weaponId)?.baseWeaponId)
      .filter((id): id is string => Boolean(id))
  );
  const optionsFor = (memberId: string, memberName: string, equipmentIds: string[]): StargraveArmouryBoostOption[] => {
    const replaced = replacedBaseWeaponIds(memberId);
    return equipmentIds
      .filter((id) => ARMOURY_BOOSTABLE_WEAPON_IDS.includes(id) && !replaced.has(id))
      .map((id) => ({ memberId, memberName, weaponId: id, weaponName: equipmentById.get(id)?.name ?? id }));
  };
  return [
    ...leaders.flatMap((leader) => optionsFor(leader.id, leader.name, leader.gearIds)),
    ...crew.soldiers
      .filter((soldier) => isFieldedStargraveSoldier(soldier) && !soldier.isTemporary)
      .flatMap((soldier) => {
        const profile = profiles.find((candidate) => candidate.id === soldier.profileId);
        return optionsFor(soldier.id, soldier.name, profile?.equipmentIds ?? []);
      }),
  ];
}
